const ACCESS_ALLOW_VALUES = ["yes", "all", "team", "own"];

export const getStoredAcl = () => {
  try {
    const storedAcl = localStorage.getItem("acl");
    if (storedAcl) return JSON.parse(storedAcl);

    const storedUser = localStorage.getItem("login_object");
    if (!storedUser) return null;

    return JSON.parse(storedUser)?.acl || null;
  } catch {
    return null;
  }
};

export const hasAcl = () => Boolean(getStoredAcl());

const isAllowedValue = (value) => {
  if (value === true) return true;
  if (value === false || value == null) return false;

  return ACCESS_ALLOW_VALUES.includes(String(value).toLowerCase());
};

export const canEntity = (entity, action = "read") => {
  const acl = getStoredAcl();
  if (!acl) return true;

  const entityAcl = acl?.table?.[entity];
  if (entityAcl === true) return true;
  if (!entityAcl) return false;

  return isAllowedValue(entityAcl?.[action]);
};

export const canRead = (entity) => canEntity(entity, "read");
export const canCreate = (entity) => canEntity(entity, "create");
export const canEdit = (entity) => canEntity(entity, "edit");
export const canDelete = (entity) => canEntity(entity, "delete");

export const getStoredUser = () => {
  try {
    const storedUser = localStorage.getItem("login_object");
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

/**
 * Prettifies a username like "chandra_vikas" → "Chandra Vikas":
 * replaces underscores with spaces and title-cases each word.
 * Returns the fallback when the input is empty / not a string.
 */
export const formatUserDisplayName = (username, fallback = "AAJneeti Team") => {
  if (typeof username !== "string" || !username.trim()) return fallback;
  return username
    .trim()
    .replace(/_/g, " ")
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const getEntityActionValue = (entity, action) => {
  const acl = getStoredAcl();
  const entityAcl = acl?.table?.[entity];

  if (!acl) return null;
  if (entityAcl === true) return "all";

  return entityAcl?.[action] || "no";
};

export const isOwnRecord = (record, user) => {
  if (!record || !user?.id) return false;

  const userId = String(user.id);

  return [
    record.assignedUserId,
    record.createdById,
    record.ownerId,
    record.userId,
  ].some((id) => id != null && String(id) === userId);
};

const isTeamRecord = (record, user) => {
  const toIdList = (...values) =>
    values
      .flatMap((value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        return [value];
      })
      .filter(Boolean)
      .map(String);

  const userTeamIds = toIdList(
    user?.teamsIds,
    user?.teamIds,
    user?.teamId,
    user?.defaultTeamId,
  );

  const recordTeamIds = toIdList(
    record?.teamsIds,
    record?.teamIds,
    record?.teamId,
    record?.defaultTeamId,
  );

  return recordTeamIds.some((teamId) =>
    userTeamIds.includes(teamId)
  );
};

export const canEntityRecord = (entity, action, record) => {
  // Owners / admins / managers can EDIT (and read/create) any record across the
  // org — regardless of who it's assigned to, its team scoping, or a missing
  // ACL. Role is read from login_object (not the ACL table), so this holds even
  // when a lead sits in a team the owner isn't in, the assigned user isn't in
  // the loaded users list, or the ACL failed to load.
  // DELETE is deliberately EXCLUDED — deleting still follows normal own/team
  // scoping for everyone, so an elevated user can't wipe any record.
  if (isElevated() && action !== "delete") return true;

  const rawAccess = getEntityActionValue(entity, action);
  const access = typeof rawAccess === "string" ? rawAccess.toLowerCase() : rawAccess;
  if (access == null) return false;
  if (access === true || access === "yes" || access === "all") return true;
  if (access === false || access === "no") return false;

  const user = getStoredUser();

  if (access === "own") return isOwnRecord(record, user);
  if (access === "team") return isOwnRecord(record, user) || isTeamRecord(record, user);

  return isAllowedValue(access);
};

export const canEditRecord = (entity, record) =>
  canEntityRecord(entity, "edit", record);

export const canDeleteRecord = (entity, record) =>
  canEntityRecord(entity, "delete", record);

export const canGlobal = (permissionKey) => {
  const acl = getStoredAcl();
  if (!acl) return true;

  return isAllowedValue(acl?.[permissionKey]);
};

export const canReadField = (entity, field) => {
  const acl = getStoredAcl();
  if (!acl) return true;

  const fieldAcl = acl?.fieldTable?.[entity]?.[field];
  if (!fieldAcl) return true;

  return isAllowedValue(fieldAcl?.read);
};

export const canEditField = (entity, field) => {
  const acl = getStoredAcl();
  if (!acl) return true;

  const fieldAcl = acl?.fieldTable?.[entity]?.[field];
  if (!fieldAcl) return true;

  return isAllowedValue(fieldAcl?.edit);
};

export const isAdminOrManager = () => {
  const user = getStoredUser();
  const role = user?.role?.toLowerCase();
  return role === 'admin' || role === 'manager';
};
export const isSupAdmin = () => {
  const user = getStoredUser();
  const role = user?.type?.toLowerCase();
  return role === 'admin';
};

// All role names on the logged-in user, normalized + lowercased. Roles live in
// EspoCRM's plural-keyed shape (`rolesNames: { id: "Owner" }`); we also accept
// singular variants for forward-compat if the auth response shape changes.
// Mirrors the extraction in components/ui/Sidebar.jsx.
export const getUserRoles = () => {
  const user = getStoredUser();
  return [
    user?.role,
    ...(Array.isArray(user?.roles) ? user.roles : []),
    ...Object.values(user?.rolesNames || {}),
    ...Object.values(user?.roleNames || {}),
  ]
    .filter(Boolean)
    .map((r) => (typeof r === 'string' ? r : r?.name || ''))
    // Trimmed as well as lowercased: role names are typed by hand in EspoCRM,
    // and every check here is an exact match, so a stray trailing space on
    // "Owner " silently failed isOwner() / isElevated() with no error.
    .map((r) => r.trim().toLowerCase())
    .filter(Boolean);
};

// True only for users holding the "Owner" role — NOT admins or managers.
// NOTE: role names from getUserRoles() are lowercased, so compare in lowercase.
export const isOwner = () => getUserRoles().includes('owner');

// "Elevated" = admin (by type) OR Owner / Manager (by role). This is the same
// set the sidebar treats as elevated, and it gates owner/manager/admin-only
// actions like "Export All". Kept as one helper so those checks stay in sync.
export const isElevated = () => {
  const roles = getUserRoles();
  return (
    isSupAdmin() ||
    roles.includes('admin') ||
    roles.includes('owner') ||
    roles.includes('manager')
  );
};

// Who can see lead STATUS HISTORY — the "was Follow up" badge in the table and
// the "Previously in status" filter. Admin (by type) or the Owner role.
//
// Managers are deliberately NOT included: this surfaces how a lead was worked
// before it was written off, which is review material rather than day-to-day
// rep tooling. Widen to isElevated() if that changes.
//
// Named rather than repeated inline in both files, so the next change to who
// qualifies is one edit and the badge and the filter can't drift apart.
export const canSeeStatusHistory = () => isSupAdmin() || isOwner();

// Who can open Team Performance.
//
// The Owner role plus admins by type. Managers are deliberately NOT included,
// matching canSeeStatusHistory above — and for the same reason: the whole page
// is derived from cStatusObj, i.e. how each lead was worked before it was
// written off. That is review material, not day-to-day rep tooling.
//
// Admins keep access alongside Owners rather than being replaced by them; an
// Owner-only gate would lock out the account that set the page up.
//
// Named separately from canSeeStatusHistory even though the rule is identical
// today, so that changing who may read a lead's history does not silently
// change who may read the team's ranking.
export const canSeeTeamPerformance = () => isSupAdmin() || isOwner();

// ---------------------------------------------------------------------------
// Restricted (masked) users
// ---------------------------------------------------------------------------
//
// Users holding one of these EspoCRM roles see customer contact details masked:
// phone numbers reduced to their first and last two digits, email addresses
// hidden, and the WhatsApp / call / mail shortcuts and CSV exports withheld.
// Assign the role in EspoCRM — adding or removing a person never needs a code
// change, and it's the same lever you'd use for the real fix (see below).
//
// Several spellings are accepted so the exact role label in Espo doesn't have
// to match a string in here character for character.
//
// ⚠️  THIS IS A UI MASK, NOT ACCESS CONTROL. The unmasked values still arrive in
// the API response and sit in the React Query cache — anyone who opens DevTools
// on a restricted account can read them. It stops casual viewing, screenshots
// and accidental sharing; it does not stop someone who goes looking. To
// actually withhold the data, restrict `phoneNumber` / `emailAddress` read
// access on the Lead entity for this role in EspoCRM so it never reaches the
// browser — this masking is then the visible half of that.
const MASKED_ROLE_NAMES = [
  "restricted view",
  "restricted",
  "restricted user",
  "masked",
];

// Accounts masked by name, matched against `login_object.username` (the
// EspoCRM userName, e.g. "arun_rana"). Lowercase entries only — the comparison
// lowercases the stored value before checking.
//
// This exists alongside the role check so masking works with no EspoCRM setup
// at all. Prefer the role for anyone added later: a username listed here stops
// masking the moment that account is renamed in Espo, silently and with no
// error to notice.
const MASKED_USERNAMES = ["aajneeti"];

export const isMaskedUser = () => {
  const username = getStoredUser()?.username?.trim?.().toLowerCase();

  // A name listed above is masked unconditionally. Naming a specific account is
  // a deliberate instruction, so it outranks the elevated escape hatch below —
  // otherwise making that account an Owner would quietly undo the masking.
  if (username && MASKED_USERNAMES.includes(username)) return true;

  // Role-based: an elevated user is never masked, so a role assigned by mistake
  // can't blind an Owner or Admin to their own data.
  if (isElevated()) return false;
  return getUserRoles().some((role) => MASKED_ROLE_NAMES.includes(role));
};
